import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { RegistrationService } from '../services/registration.service';

export class ProfessionalEmailValidator {
  
  /**
   * Async validator for professional email addresses
   * Checks if email is professional and not from common free providers
   */
  static createValidator(registrationService: RegistrationService): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const email = control.value;

      // Don't validate empty values (let required validator handle that)
      if (!email) {
        return of(null);
      }

      // Basic email format check first
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return of({ invalidEmailFormat: true });
      }

      // Add slight delay to avoid too many API calls while user is typing
      return timer(300).pipe(
        switchMap(() => registrationService.checkEmail(email)),
        map(result => {
          // Accepter tous les emails valides (professional ET freemail)
          if (result.emailType === 'invalid') {
            return { 
              invalidEmail: { 
                message: 'Invalid email format'
              }
            };
          }
          return null; // Valid email (professional ou freemail)
        }),
        catchError(() => {
          // If API call fails, don't block the form but log the error
          return of({ emailValidationError: true });
        })
      );
    };
  }

  /**
   * Synchronous validator for basic email format
   * Now accepts both professional and personal emails
   */
  static basicEmailFormat(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    
    if (!email) {
      return null;
    }

    // Basic email format validation only
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { invalidEmailFormat: true };
    }

    // All valid email formats are now accepted (professional + personal)
    return null;
  }

  /**
   * @deprecated Use basicEmailFormat instead - keeping for backward compatibility
   */
  static basicProfessionalEmail(control: AbstractControl): ValidationErrors | null {
    // Now just delegates to basicEmailFormat
    return ProfessionalEmailValidator.basicEmailFormat(control);
  }

  /**
   * Validator for password confirmation
   */
  static matchPassword(passwordField: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.parent?.get(passwordField)?.value;
      const confirmPassword = control.value;

      if (!password || !confirmPassword) {
        return null;
      }

      if (password !== confirmPassword) {
        return { passwordMismatch: true };
      }

      return null;
    };
  }

  /**
   * Validator for password strength
   */
  static passwordStrength(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    
    if (!password) {
      return null;
    }

    const errors: { [key: string]: boolean } = {};
    let score = 0;

    // Length check
    if (password.length < 8) {
      errors['minLength'] = true;
    } else {
      score += 1;
    }

    // Uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors['requireUppercase'] = true;
    } else {
      score += 1;
    }

    // Lowercase letter
    if (!/[a-z]/.test(password)) {
      errors['requireLowercase'] = true;
    } else {
      score += 1;
    }

    // Number
    if (!/\d/.test(password)) {
      errors['requireNumber'] = true;
    } else {
      score += 1;
    }

    // Special character
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors['requireSpecialChar'] = true;
    } else {
      score += 1;
    }

    // Return errors if password is too weak
    if (Object.keys(errors).length > 0) {
      return { passwordStrength: { errors, score } };
    }

    return null;
  }

  /**
   * Validator for email list (invite colleagues)
   */
  static emailList(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    
    if (!value?.trim()) {
      return null; // Optional field
    }

    const emails = value
      .split(/[,\n\r]+/)
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((email: string) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      return { 
        invalidEmailList: { 
          invalidEmails: invalidEmails,
          message: `Invalid email addresses: ${invalidEmails.join(', ')}`
        }
      };
    }

    return null;
  }
}